/**
 * Utility Functions
 *
 * Common utility functions used across the application.
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with clsx
 *
 * Combines clsx for conditional classes and tailwind-merge
 * to handle Tailwind class conflicts intelligently.
 *
 * @param inputs - Class values (strings, objects, arrays)
 * @returns Merged class string
 *
 * @example
 * ```tsx
 * cn('px-2 py-1', 'bg-blue-500', { 'text-white': isActive })
 * cn('px-2', 'px-4') // Returns 'px-4' (last one wins)
 * ```
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Validates that a URL is safe for use in href (prevents javascript: and other protocol injection).
 * Only allows http and https protocols.
 */
export function isSafeExternalUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (!(trimmed.startsWith('https://') || trimmed.startsWith('http://'))) return false;
  return true;
}

/**
 * Formats a category string (snake_case) to Title Case with spaces.
 * Example: 'best_practices' -> 'Best Practices'
 */
export function formatCategoryName(category: string): string {
  if (!category) return '';
  return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}
